import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  CreateButton,
  EditButton,
  ViewButton,
  DeleteButton,
  ExportButton,
  ActionButtonGroup
} from './ui/ActionButtons';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Search, Filter, Calendar, CheckCircle2, Clock } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { useSellers } from '../hooks/useSellers';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { TableExportMenu } from './ui/TableExportMenu';
import { X } from 'lucide-react';
import { TableFilterBar } from './ui/TableFilterBar';
import { TablePeriodSelector } from './ui/TablePeriodSelector';
import { DownloadReceiptButton } from './ui/DownloadReceiptButton';
import { DetalleVentaLote } from '../utils/receiptTemplates';
import { matchesMonthYear, matchesYear } from '../utils/dateFilters';

export function LotTable({ setActiveTab }: { setActiveTab?: (tab: string) => void }) {
  const { lots, expenses, returnLot, comisiones, fetchLots, fetchComisiones, fetchMovimientos, config } = useAppData();
  const { sellers } = useSellers();
  console.log('Lotes recibidos del contexto:', lots);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterTipo, setFilterTipo] = useState('Todos');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, lotes: string[] | null, mov?: any }>({ open: false, lotes: null });
  const [blockedDelete, setBlockedDelete] = useState<{ open: boolean, message: string } | null>(null);
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState('Todos');
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Adaptar los datos reales
  const filteredData = [...lots].reverse().filter((lote: any) => {
    const matchesSearch =
      lote.buyerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lote.seller?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lote.id?.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesDate = true;
    if (filterDate) {
      const saleDate = lote.saleDate ? new Date(lote.saleDate).toISOString().split('T')[0] : '';
      matchesDate = saleDate === filterDate;
    }
    let matchesTipo = true;
    if (filterTipo !== 'Todos') {
      matchesTipo = (lote.paymentType || '').toLowerCase() === filterTipo.toLowerCase();
    }

    // Filtrado por mes y año mejorado
    let matchesMonthFilter = true;
    if (selectedMonth === 'Todos') {
      // Si es "Todos" los meses, filtrar solo por año
      matchesMonthFilter = matchesYear(lote.saleDate, selectedYear);
    } else {
      // Si es un mes específico, filtrar por mes Y año
      matchesMonthFilter = matchesMonthYear(lote.saleDate, selectedMonth, selectedYear);
    }

    return matchesSearch && matchesDate && matchesTipo && matchesMonthFilter;
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      'vendido': 'default',
      'disponible': 'secondary',
      'crédito': 'destructive'
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants] || 'default'}>
        {status}
      </Badge>
    );
  };

  const getPaymentTypeBadge = (type: string) => {
    const variants = {
      'contado': 'default',
      'pagos': 'secondary'
    } as const;

    return (
      <Badge variant={variants[type as keyof typeof variants] || 'default'}>
        {type}
      </Badge>
    );
  };

  const getMovimientos = () => {
    const movimientos: any[] = [];
    filteredData.forEach((lote: any) => {
      const key = [lote.seller, lote.buyerName, lote.buyerDPI, lote.phoneNumber, lote.saleDate, lote.paymentType, lote.depositSlip, lote.receiptNumber].join('|');
      let mov = movimientos.find(m => m.key === key);
      if (!mov) {
        mov = { ...lote, lotes: [lote.id], key };
        mov.lotValue = lote.lotValue || 0;
        mov.amountPaid = lote.amountPaid || 0;
        mov.totalValue = lote.totalValue || 0;
        movimientos.push(mov);
      } else {
        mov.lotes.push(lote.id);
        mov.lotValue = (mov.lotValue || 0) + (lote.lotValue || 0);
        mov.amountPaid = (mov.amountPaid || 0) + (lote.amountPaid || 0);
        mov.totalValue = (mov.totalValue || 0) + (lote.totalValue || 0);
      }
    });
    return movimientos;
  };

  const exportToExcel = () => {
    const movimientos = getMovimientos()
      // Aplicar el mismo ordenamiento que la tabla visual
      .sort((a, b) => {
        const dateA = new Date(a.saleDate).getTime();
        const dateB = new Date(b.saleDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (createdA !== createdB) return createdB - createdA;
        if (a.id < b.id) return 1;
        if (a.id > b.id) return -1;
        return 0;
      });
    const ws = XLSX.utils.json_to_sheet(movimientos.map(flattenRow));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lotes');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    // Nombre del archivo con fecha actual
    const fecha = new Date();
    const nombreArchivo = `lotes_${fecha.getDate()}-${fecha.getMonth() + 1}-${fecha.getFullYear()}.xlsx`;
    FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), nombreArchivo);
  };

  const exportToCSV = () => {
    const movimientos = getMovimientos()
      // Aplicar el mismo ordenamiento que la tabla visual
      .sort((a, b) => {
        const dateA = new Date(a.saleDate).getTime();
        const dateB = new Date(b.saleDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (createdA !== createdB) return createdB - createdA;
        if (a.id < b.id) return 1;
        if (a.id > b.id) return -1;
        return 0;
      });
    const ws = XLSX.utils.json_to_sheet(movimientos.map(flattenRow));
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    // Nombre del archivo con fecha actual
    const fecha = new Date();
    const nombreArchivo = `lotes_${fecha.getDate()}-${fecha.getMonth() + 1}-${fecha.getFullYear()}.csv`;
    FileSaver.saveAs(blob, nombreArchivo);
  };

  const exportToPDF = () => {
    const movimientos = getMovimientos()
      // Aplicar el mismo ordenamiento que la tabla visual
      .sort((a, b) => {
        const dateA = new Date(a.saleDate).getTime();
        const dateB = new Date(b.saleDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (createdA !== createdB) return createdB - createdA;
        if (a.id < b.id) return 1;
        if (a.id > b.id) return -1;
        return 0;
      });
    const doc = new jsPDF();
    doc.text('Lotes Registrados', 14, 16);
    autoTable(doc, {
      head: [[
        'ID Lote(s)', 'Vendedor', 'Comprador', 'DPI', 'Teléfono', 'Fecha Venta',
        'Tipo de Venta', 'No. de cuotas', 'Boleta de pago', 'No. Doc.', 'No. Autorización',
        'Valor Lote', 'Enganche', 'Abonos realizados', 'Total pagado', 'Saldo Actual'
      ]],
      body: movimientos.map(flattenRowArr),
      startY: 22,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [22, 160, 133] },
    });
    // Nombre del archivo con fecha actual
    const fecha = new Date();
    const nombreArchivo = `lotes_${fecha.getDate()}-${fecha.getMonth() + 1}-${fecha.getFullYear()}.pdf`;
    doc.save(nombreArchivo);
  };

  function flattenRow(row: any) {
    // Calcular valores de la misma manera que la tabla visual
    const enganche = parseFloat(row.payments?.find((p: any) => p.type === 'enganche')?.amount || 0);
    const abonos = row.payments?.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0) || 0;
    const totalPagado = row.paymentType === 'contado' ? (row.totalValue || 0) : (enganche + abonos);
    const saldoPendiente = (row.totalValue || 0) - totalPagado;

    return {
      'ID Lote(s)': Array.isArray(row.lotes) ? row.lotes.join(', ') : row.id,
      'Vendedor': row.seller,
      'Comprador': row.buyerName,
      'DPI': row.buyerDPI,
      'Teléfono': row.phoneNumber,
      'Fecha Venta': row.saleDate,
      'Tipo de Venta': row.paymentType === 'contado' ? 'Contado' : 'Crédito',
      'No. de cuotas': row.numberOfPayments || row.numeroPagos || '-',
      'Boleta de pago': row.depositSlip,
      'No. Doc.': row.numeroDocumento || '-',
      'No. Autorización': row.transferencia || row.receiptNumber || '-',
      'Valor Lote': row.lotValue,
      'Enganche': enganche,
      'Abonos realizados': abonos,
      'Total pagado': totalPagado,
      'Saldo Actual': saldoPendiente,
    };
  }
  function flattenRowArr(row: any) {
    const r = flattenRow(row);
    return Object.values(r);
  }

  // Agrega función utilitaria para formato Guatemala
  function formatGTDate(dateStr: string) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Acción de eliminar movimiento
  const handleDeleteMovimiento = async (loteIds: string[], mov?: any) => {
    // Buscar comisión asociada
    let lotGroupId = mov?.id || (Array.isArray(mov?.idLotes) && mov.idLotes.length > 1 ? mov.idLotes.join('-') : loteIds[0]);
    let comision = comisiones.find((c: any) => {
      // Coincide por grupo o por lote individual
      if (c.lotId === lotGroupId || (typeof c.lotNumber === 'string' && c.lotNumber.split(/,|-/).map((s: string) => s.trim()).includes(lotGroupId))) return true;
      if (Array.isArray(mov?.idLotes) && c.lotNumber) {
        // Coincidencia por todos los lotes del grupo
        const lotesCom = c.lotNumber.split(/,|-/).map((s: string) => s.trim());
        return mov.idLotes.every((id: string) => lotesCom.includes(id));
      }
      return false;
    });
    if (comision && comision.paid) {
      setBlockedDelete({ open: true, message: 'No se puede eliminar el lote porque la comisión ya fue pagada. Realice primero la devolución.' });
      setDeleteDialog({ open: false, lotes: null });
      return;
    }
    // Si no hay comisión pagada, proceder como antes
    try {
      if (mov && Array.isArray(mov.idLotes) && mov.idLotes.length > 1) {
        await returnLot(mov.id);
      } else {
        await Promise.all(loteIds.map(async (id) => await returnLot(id)));
      }
      // Refrescar todos los datos globales tras la eliminación
      await Promise.all([
        fetchLots(),
        fetchComisiones(),
        fetchMovimientos()
      ]);
    } catch (err) {
      alert('Ocurrió un error al devolver el/los lote(s). Intenta de nuevo.');
    }
    setDeleteDialog({ open: false, lotes: null });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Lotes Registrados</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Gestiona y visualiza todos los lotes registrados en el sistema</p>
        </div>
        <CreateButton
          className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
          onClick={() => {
            if (typeof setActiveTab === 'function') {
              setActiveTab('registro');
            } else {
              alert('Función de navegación no disponible. Integra con el sistema de tabs principal.');
            }
          }}
        >
          Registrar Nuevo Lote
        </CreateButton>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 mb-6 px-1">
        {/* Buscar */}
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70" />
          <Input
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground placeholder:text-muted-foreground/40 shadow-none transition-colors"
          />
          {searchTerm && (
            <button
              type="button"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-destructive focus:outline-none"
              onClick={() => setSearchTerm('')}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Fecha */}
        <div className="relative flex items-center">
          <Input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            className="bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pr-8 [color-scheme:light] dark:[color-scheme:dark]"
          />
          {filterDate && (
            <button
              type="button"
              className="absolute right-2 text-muted-foreground hover:text-destructive focus:outline-none"
              onClick={() => setFilterDate('')}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Tipo */}
        <div className="relative">
          <select
            value={filterTipo}
            onChange={e => setFilterTipo(e.target.value)}
            className="bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[100px]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '10px'
            }}
          >
            <option value="Todos">Todos</option>
            <option value="contado">Contado</option>
            <option value="pagos">Crédito</option>
          </select>
        </div>

        <div className="flex-1" />

        {/* Exportar */}
        <TableExportMenu
          onExcel={exportToExcel}
          onCSV={exportToCSV}
          onPDF={exportToPDF}
        />
      </div>

      {/* Periodo de Consulta */}
      <TablePeriodSelector
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
      />

      {/* Tabla */}
      <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
        <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm font-medium tracking-tight">Lotes ({filteredData.length})</CardTitle>
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
              {filteredData.length} de {lots.length}
            </span>
          </div>
        </CardHeader>
        <CardContent className="bg-card p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border/50">
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10 pl-4">Orden</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">ID Lote(s)</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Vendedor</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Comprador</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">DPI</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Teléfono</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Fecha Venta</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Tipo de Venta</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">No. de cuotas</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Boleta de pago</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">No. Doc.</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">No. Autorización</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Valor Lote(s)</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Enganche</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Abonos realizados</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Total pagado</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10">Saldo pendiente</TableHead>
                  <TableHead className="text-[11px] font-medium text-muted-foreground h-10 pr-4 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getMovimientos()
                  // Ordenar por saleDate descendente y luego por createdAt descendente
                  .sort((a, b) => {
                    const dateA = new Date(a.saleDate).getTime();
                    const dateB = new Date(b.saleDate).getTime();
                    if (dateA !== dateB) return dateB - dateA;
                    // Si la fecha de venta es igual, ordenar por createdAt descendente
                    const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    if (createdA !== createdB) return createdB - createdA;
                    // Si no hay createdAt o es igual, usar id descendente
                    if (a.id < b.id) return 1;
                    if (a.id > b.id) return -1;
                    return 0;
                  })
                  // Filtrar duplicados: solo mostrar el grupo si existe idLotes, o el individual si no está en ningún grupo
                  .filter((mov, idx, arr) => {
                    if (Array.isArray(mov.idLotes) && mov.idLotes.length > 1) {
                      // Es grupo: mostrar solo el grupo
                      return true;
                    }
                    // Es individual: mostrar solo si no está en ningún grupo
                    const perteneceAGrupo = arr.some(m2 => Array.isArray(m2.idLotes) && m2.idLotes.includes(mov.id));
                    return !perteneceAGrupo;
                  })
                  .filter(mov => mov.lotes.some((id: string) => {
                    const lot = lots.find((l: any) => l.id === id);
                    return lot && lot.status !== 'disponible';
                  }))
                  .map((mov, index) => {
                    // Tipo de Venta
                    const tipoVenta = mov.paymentType === 'contado' ? 'Contado' : 'Crédito';
                    const tipoVentaColor = mov.paymentType === 'contado' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300';
                    // Tipo de Pago (solo para crédito)
                    let tipoPago = '';
                    let tipoPagoColor = '';
                    if (mov.paymentType === 'pagos') {
                      // Si solo hay un pago y es menor al total, es enganche
                      tipoPago = mov.amountPaid && mov.amountPaid < mov.totalValue ? 'Enganche' : 'Abono';
                      tipoPagoColor = tipoPago === 'Enganche' ? 'bg-black text-white' : 'bg-gray-200 text-gray-800';
                    } else {
                      tipoPago = 'Contado';
                      tipoPagoColor = 'bg-green-100 text-green-700 border-green-300';
                    }

                    // Calcular saldo pendiente para identificar lotes completamente pagados
                    let totalPagado = 0;
                    if (mov.paymentType === 'contado') {
                      totalPagado = mov.totalValue || 0;
                    } else {
                      const enganche = parseFloat(mov.payments?.find((p: any) => p.type === 'enganche')?.amount || 0);
                      const abonos = mov.payments?.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0) || 0;
                      totalPagado = enganche + abonos;
                    }
                    const saldoPendiente = (mov.totalValue || 0) - totalPagado;
                    const isPaid = saldoPendiente === 0;

                    // Saldo actual
                    const saldoActual = (mov.totalValue || 0) - (mov.amountPaid || 0);
                    // Mostrar ID concatenado si es grupo, o simple si es individual
                    const idLotesStr = Array.isArray(mov.idLotes) && mov.idLotes.length > 1 ? mov.idLotes.join('-') : mov.id;
                    return (
                      <TableRow
                        key={index}
                        className={`border-b border-border/50 hover:bg-muted/50 transition-colors group ${isPaid
                          ? 'bg-purple-50/5'
                          : ''
                          }`}
                      >
                        <TableCell className="font-mono text-[13px] text-muted-foreground group-hover:text-foreground transition-colors pl-4 py-3">{index + 1}</TableCell>
                        <TableCell className="font-mono text-[13px] font-medium py-3">{idLotesStr}</TableCell>
                        <TableCell className="text-[13px] py-3">{
                          sellers.find((s: any) => s.id === mov.seller)?.name
                          || sellers.find((s: any) => s.account === mov.seller)?.name
                          || sellers.find((s: any) => s.name === mov.seller)?.name
                          || mov.seller || '-'
                        }</TableCell>
                        <TableCell className="text-[13px] py-3">{mov.buyerName || '-'}</TableCell>
                        <TableCell className="font-mono text-[13px] py-3">{mov.buyerDPI || '-'}</TableCell>
                        <TableCell className="font-mono text-[13px] py-3">{mov.phoneNumber || '-'}</TableCell>
                        <TableCell className="text-[13px] py-3">{formatGTDate(mov.saleDate)}</TableCell>
                        <TableCell className="text-[13px] py-3">
                          {mov.paymentType === 'contado' ? (
                            <div className="flex items-center gap-1.5 text-emerald-500">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <span className="text-[13px] font-medium">Contado</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-amber-500">
                              <Clock className="h-3.5 w-3.5" />
                              <span className="text-[13px] font-medium">Crédito</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-[13px] py-3">
                          {/* Mostrar número de cuotas pactadas */}
                          {mov.numberOfPayments || mov.numeroPagos || '-'}
                        </TableCell>
                        <TableCell className="font-mono text-[13px] py-3">{mov.depositSlip || '-'}</TableCell>
                        <TableCell className="font-mono text-[13px] py-3">{mov.numeroDocumento || '-'}</TableCell>
                        <TableCell className="font-mono text-[13px] py-3">{mov.transferencia || mov.receiptNumber || '-'}</TableCell>
                        <TableCell className="font-mono text-[13px] py-3">Q{(() => {
                          // Si es grupo, mostrar valor total (valor lote * cantidad de lotes)
                          if (Array.isArray(mov.idLotes) && mov.idLotes.length > 1) {
                            return (mov.lotValue * mov.idLotes.length).toLocaleString();
                          }
                          // Si es individual, mostrar valor individual
                          return (mov.lotValue || 0).toLocaleString();
                        })()}</TableCell>
                        <TableCell className="font-mono text-[13px] py-3 text-emerald-500">
                          {/* Mostrar solo el enganche inicial */}
                          Q {parseFloat(mov.payments?.find((p: any) => p.type === 'enganche')?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="font-mono text-[13px] py-3 text-emerald-500">
                          {/* Sumar todos los abonos */}
                          Q {mov.payments?.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-[13px] py-3 text-emerald-500">
                          {/* Total pagado: contado = totalValue, crédito = enganche + abonos */}
                          {(() => {
                            if (mov.paymentType === 'contado') {
                              return `Q ${(mov.totalValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                            } else {
                              const enganche = parseFloat(mov.payments?.find((p: any) => p.type === 'enganche')?.amount || 0);
                              const abonos = mov.payments?.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0) || 0;
                              return `Q ${(enganche + abonos).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                            }
                          })()}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-[13px] py-3 text-rose-500">
                          {/* Saldo pendiente: valor total - total pagado */}
                          {(() => {
                            let totalPagado = 0;
                            if (mov.paymentType === 'contado') {
                              totalPagado = mov.totalValue || 0;
                            } else {
                              const enganche = parseFloat(mov.payments?.find((p: any) => p.type === 'enganche')?.amount || 0);
                              const abonos = mov.payments?.filter((p: any) => p.type === 'abono').reduce((sum: number, p: any) => sum + Number(p.amount), 0) || 0;
                              totalPagado = enganche + abonos;
                            }
                            return `Q ${((mov.totalValue || 0) - totalPagado).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                          })()}
                        </TableCell>
                        <TableCell className="py-3 pr-4">
                          <ActionButtonGroup>
                            {/* Botón de descarga de recibo - siempre visible */}
                            <DownloadReceiptButton
                              empresaInfo={{
                                razonSocial: config.razonSocial || 'Bimobi',
                                nit: config.nit,
                                direccionCompleta: config.direccionCompleta || config.ubicacion || 'Guatemala',
                                telefono1: config.telefono1 || '',
                                telefono2: config.telefono2,
                                email: config.email,
                                logoUrl: config.logoUrl
                              }}
                              detalleRecibo={{
                                tipoRecibo: 'venta_lote',
                                cliente: {
                                  nombre: mov.buyerName || 'Sin nombre',
                                  dpi: mov.buyerDPI,
                                  telefono: mov.phoneNumber
                                },
                                lotes: Array.isArray(mov.idLotes) ? mov.idLotes.join(', ') : mov.id,
                                vendedor: sellers.find((s: any) => s.id === mov.seller)?.name
                                  || sellers.find((s: any) => s.account === mov.seller)?.name
                                  || sellers.find((s: any) => s.name === mov.seller)?.name
                                  || mov.seller || 'Sin vendedor',
                                fechaVenta: mov.saleDate,
                                tipoPago: mov.paymentType,
                                valorTotal: mov.totalValue || 0,
                                enganche: mov.paymentType === 'pagos'
                                  ? parseFloat(mov.payments?.find((p: any) => p.type === 'enganche')?.amount || 0)
                                  : undefined,
                                numeroCuotas: mov.paymentType === 'pagos'
                                  ? (mov.numberOfPayments || mov.numeroPagos)
                                  : undefined,
                                montoCuota: mov.paymentType === 'pagos' && mov.numberOfPayments
                                  ? ((mov.totalValue || 0) - parseFloat(mov.payments?.find((p: any) => p.type === 'enganche')?.amount || 0)) / (mov.numberOfPayments || 1)
                                  : undefined,
                                boletaDeposito: mov.depositSlip || mov.transferencia,
                                reciboCaja: mov.receiptNumber
                              } as Omit<DetalleVentaLote, 'numeroRecibo' | 'fechaEmision'>}
                              fileName={`Recibo-Venta-${Array.isArray(mov.idLotes) ? mov.idLotes.join('-') : mov.id}`}
                              onSuccess={(numeroRecibo) => {
                                console.log('Recibo generado:', numeroRecibo);
                              }}
                            />

                            {/* Botones de editar y eliminar - solo si no es automático */}
                            {!mov.automatic && (
                              <>
                                <EditButton onClick={() => {
                                  if (typeof setActiveTab === 'function') {
                                    // Navegar a registro y pasar los IDs de los lotes para edición
                                    window.sessionStorage.setItem('editLotes', JSON.stringify(mov.lotes));
                                    setActiveTab('registro');
                                  }
                                }} />
                                <DeleteButton onClick={() => { setDeleteDialog({ open: true, lotes: mov.lotes, mov }); }} />
                              </>
                            )}
                          </ActionButtonGroup>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>

          {filteredData.length === 0 && (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No se encontraron registros que coincidan con tu búsqueda</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de confirmación de eliminación */}
      {deleteDialog.open && (
        <Dialog open={deleteDialog.open} onOpenChange={(v: boolean) => { if (!v) setDeleteDialog({ open: false, lotes: null }); }}>
          <DialogContent className="max-w-md bg-popover border border-border text-foreground">
            <DialogHeader>
              <DialogTitle>¿Eliminar movimiento?</DialogTitle>
              <DialogDescription>
                Esta acción devolverá los lotes seleccionados a estado disponible y eliminará toda la información de comprador y pagos. Confirma si deseas continuar.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 text-foreground">
              <span>Esto devolverá los lotes <span className="font-bold text-foreground">{deleteDialog.lotes?.join(', ')}</span> a estado <span className="font-bold">disponible</span> y eliminará toda la información de comprador y pagos. ¿Deseas continuar?</span>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, lotes: null })}>Cancelar</Button>
              <Button variant="destructive" onClick={() => {
                if (deleteDialog.lotes) {
                  handleDeleteMovimiento(deleteDialog.lotes || [], deleteDialog.mov);
                } else {
                  setDeleteDialog({ open: false, lotes: null });
                }
              }}>Eliminar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Diálogo de bloqueo por comisión pagada */}
      {blockedDelete?.open && (
        <Dialog open={blockedDelete.open} onOpenChange={() => setBlockedDelete(null)}>
          <DialogContent className="max-w-md bg-popover border border-border text-foreground">
            <DialogHeader>
              <DialogTitle>Eliminación bloqueada</DialogTitle>
              <DialogDescription>{blockedDelete.message}</DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setBlockedDelete(null)}>Cerrar</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}